import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Phone, ChevronRight } from 'lucide-react';
import SEOHead from '../components/SEO/SEOHead';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ThemeProvider } from '../hooks/useTheme';

interface ServicePageLayoutProps {
  title: string;
  description: string;
  canonical: string;
  children: ReactNode;
  ctaText?: string;
  relatedServices?: { path: string; label: string }[];
}

export default function ServicePageLayout({
  title,
  description,
  canonical,
  children,
  ctaText = "Discutons de votre projet",
  relatedServices = [],
}: ServicePageLayoutProps) {
  return (
    <ThemeProvider>
      <SEOHead
        title={title}
        description={description}
        canonical={canonical}
      />
      <Navigation />
      <div className="min-h-screen bg-warm-gray-50 dark:bg-warm-gray-900 overflow-x-hidden transition-colors duration-300">
        {/* Hero section de la page de service */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-aurore-pale to-aurore-light/30 dark:from-warm-gray-900 dark:to-warm-gray-800">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <Link
                to="/"
                className="inline-flex items-center text-aurore-dark dark:text-aurore-light hover:underline mb-6 text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Link>
              <h1 className="text-4xl md:text-5xl font-bold text-warm-gray-900 dark:text-warm-gray-100 leading-tight mb-6">
                {title.split(' | ')[0]}
              </h1>
              <p className="text-xl text-warm-gray-700 dark:text-warm-gray-300 leading-relaxed">
                {description}
              </p>
            </div>
          </div>
        </section>

        {/* Contenu principal */}
        <main className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              {children}
            </div>
          </div>
        </main>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-aurore-dark/90 via-aurore-medium/90 to-aurore-light dark:from-warm-gray-800 dark:via-warm-gray-800 dark:to-warm-gray-700">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {ctaText}
              </h2>
              <p className="text-xl text-white/95 dark:text-warm-gray-300 mb-10 leading-relaxed">
                Premier échange gratuit de 30 minutes. Vous m'expliquez votre situation, je vous dis si je peux vous aider et comment.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://calendly.com/aurore-rh/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center space-x-2 bg-white dark:bg-aurore-dark text-aurore-dark dark:text-white px-8 py-4 rounded-2xl hover:bg-aurore-pale dark:hover:bg-aurore-medium transition-all duration-500 font-medium shadow-md"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Prendre rendez-vous</span>
                </a>
                <a
                  href="tel:+33750717728"
                  className="inline-flex items-center justify-center space-x-2 bg-transparent text-white px-8 py-4 rounded-2xl border-2 border-white/40 hover:border-white hover:bg-white/10 transition-all duration-500 font-medium"
                >
                  <Phone className="w-5 h-5" />
                  <span>07 50 71 77 28</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Liens internes vers autres services */}
        {relatedServices.length > 0 && (
          <section className="py-16 bg-white dark:bg-warm-gray-900">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-warm-gray-900 dark:text-warm-gray-100 mb-8 text-center">
                  Découvrez nos autres services
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedServices.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className="group flex items-center justify-between p-5 bg-warm-gray-50 dark:bg-warm-gray-800 rounded-2xl hover:bg-aurore-light/30 dark:hover:bg-warm-gray-700 transition-all duration-300 border border-transparent hover:border-aurore-light dark:hover:border-warm-gray-600"
                    >
                      <span className="font-medium text-warm-gray-800 dark:text-warm-gray-200 group-hover:text-aurore-dark dark:group-hover:text-aurore-light transition-colors">
                        {service.label}
                      </span>
                      <ChevronRight className="w-5 h-5 text-warm-gray-400 group-hover:text-aurore-dark dark:group-hover:text-aurore-light group-hover:translate-x-1 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </ThemeProvider>
  );
}
